import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApi } from '@/lib/hooks';
import { fr } from '@/i18n/fr';
import { dateTime } from '@/lib/format';
import { ShieldCheck } from 'lucide-react';
import { Banner, Button, Field, Input, Spinner } from '@/components/ui/core';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-window px-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/favicon.svg" alt="" className="mb-4 size-12" />
          <h1 className="text-title font-semibold">SaaS Manager</h1>
          <p className="mt-1 text-muted">Pilotage, sauvegarde et reprise de vos SaaS.</p>
        </div>
        <div className="glass rounded-[28px] p-7">{children}</div>
      </div>
    </div>
  );
}

export function GithubMark() {
  return (
    <svg viewBox="0 0 16 16" className="size-4" aria-hidden fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

export function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="size-4" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

export function LoginPage() {
  const { me, loading, refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const providers = useApi<{ password: boolean; github: boolean; google: boolean }>('/auth/providers');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(params.get('error'));
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner />;
  if (me && (me.mfa === 'verified' || me.mfa === 'not_required')) return <Navigate to="/" replace />;
  if (me) return <Navigate to="/mfa" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ mfa: string }>('/auth/login', { email, password });
      await refresh();
      navigate(res.mfa === 'verified' || res.mfa === 'not_required' ? '/' : '/mfa', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  }

  const p = providers.data;
  const oauth = p && (p.github || p.google);
  return (
    <Frame>
      {error && <Banner tone="critical">{error}</Banner>}
      {oauth && (
        <div className="flex flex-col gap-2">
          {p.github && (
            <a href="/api/v1/auth/oauth/github/start" className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-surface-solid font-semibold ring-1 ring-hairline-strong hover:bg-surface-2">
              <GithubMark /> Continuer avec GitHub
            </a>
          )}
          {p.google && (
            <a href="/api/v1/auth/oauth/google/start" className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-surface-solid font-semibold ring-1 ring-hairline-strong hover:bg-surface-2">
              <GoogleMark /> Continuer avec Google
            </a>
          )}
          <p className="mt-1 text-center text-caption text-muted">Réservé aux comptes invités, reconnus par leur adresse e-mail vérifiée.</p>
        </div>
      )}
      {oauth && p.password && (
        <div className="my-4 flex items-center gap-3 text-caption text-faint" aria-hidden>
          <span className="h-px flex-1 bg-hairline" />
          ou
          <span className="h-px flex-1 bg-hairline" />
        </div>
      )}
      {(!p || p.password) && (
      <form onSubmit={submit} noValidate>
        <Field label="Adresse e-mail" htmlFor="email">
          <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Mot de passe" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" variant={oauth ? 'secondary' : 'primary'} className="mt-2 w-full" loading={busy}>
          Se connecter
        </Button>
      </form>
      )}
    </Frame>
  );
}

interface InvitationPreview {
  email: string | null;
  name: string | null;
  role: 'admin' | 'member';
  expiresAt: string;
  invitedBy: string | null;
  providers: { github: boolean; google: boolean };
}

/** Public page behind an invitation link: accepting = signing in with GitHub or Google. */
export function InvitePage() {
  const { token = '' } = useParams();
  const { me, loading } = useSession();
  const inv = useApi<InvitationPreview>(`/auth/invitations/${encodeURIComponent(token)}`, { retry: false });
  if (loading || inv.isLoading) return <Spinner />;
  if (me) return <Navigate to="/" replace />;
  if (inv.error) {
    return (
      <Frame>
        <Banner tone="critical" title="Invitation inutilisable">{inv.error.message}</Banner>
        <a href="/login" className="mt-2 block text-center text-link">Aller à la page de connexion</a>
      </Frame>
    );
  }
  const d = inv.data!;
  const start = (p: 'github' | 'google') => `/api/v1/auth/oauth/${p}/start?invite=${encodeURIComponent(token)}`;
  return (
    <Frame>
      <h2 className="text-lg font-semibold">{d.name ? `Bienvenue, ${d.name}` : 'Vous êtes invité'}</h2>
      <p className="mt-1 mb-5 text-muted">
        {d.invitedBy ?? 'Le propriétaire'} vous donne accès à la console en tant que <strong className="text-ink">{fr.role[d.role]}</strong>.
        {d.email ? <> Connectez-vous avec le compte <strong className="text-ink">{d.email}</strong>.</> : ' Connectez-vous avec votre compte professionnel.'}
      </p>
      <div className="flex flex-col gap-2">
        {d.providers.google && (
          <a href={start('google')} className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-surface-solid font-semibold ring-1 ring-hairline-strong hover:bg-surface-2">
            <GoogleMark /> Continuer avec Google
          </a>
        )}
        {d.providers.github && (
          <a href={start('github')} className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-surface-solid font-semibold ring-1 ring-hairline-strong hover:bg-surface-2">
            <GithubMark /> Continuer avec GitHub
          </a>
        )}
        {!d.providers.google && !d.providers.github && <Banner tone="warning">Aucune connexion Google ou GitHub n’est configurée sur cette console : demandez au propriétaire de créer votre compte directement.</Banner>}
      </div>
      <p className="mt-4 text-center text-caption text-muted">Lien à usage unique, valable jusqu’au {dateTime(d.expiresAt)}. La double authentification vous sera demandée pour un compte administrateur.</p>
    </Frame>
  );
}

export function MfaPage() {
  const { me, loading, refresh } = useSession();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const enrolling = me?.mfa === 'setup_required';

  useEffect(() => {
    if (enrolling && !setup) {
      api.post<{ secret: string; qr: string }>('/auth/mfa/setup').then(setSetup).catch((e: ApiError) => setError(e.message));
    }
  }, [enrolling, setup]);

  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.mfa === 'verified' || me.mfa === 'not_required') return <Navigate to="/" replace />;

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/mfa/verify', { code });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Vérification impossible.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame>
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="size-5 text-link" aria-hidden />
        <h2 className="font-semibold">{enrolling ? 'Activez la double authentification' : 'Double authentification'}</h2>
      </div>
      {enrolling ? (
        <>
          <p className="mb-4 text-muted">Les comptes à privilèges doivent utiliser une application d’authentification. Scannez ce code, puis saisissez le code à six chiffres affiché.</p>
          {setup ? (
            <div className="mb-4 flex flex-col items-center gap-3">
              <img src={setup.qr} alt="QR code de configuration" className="size-44 rounded-[20px] bg-white p-3" />
              <details className="w-full text-caption text-muted">
                <summary className="cursor-pointer">Saisir la clé manuellement</summary>
                <code className="mt-2 block break-all rounded-md bg-surface-2 p-2 font-mono select-all">{setup.secret}</code>
              </details>
            </div>
          ) : (
            <Spinner label="Préparation" />
          )}
        </>
      ) : (
        <p className="mb-4 text-muted">Saisissez le code affiché par votre application d’authentification.</p>
      )}
      <form onSubmit={verify}>
        {error && <Banner tone="critical">{error}</Banner>}
        <Field label="Code" htmlFor="code">
          <Input
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            autoFocus
            className="text-center font-mono text-lg tracking-[0.4em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={busy} disabled={code.length !== 6}>
          Vérifier
        </Button>
      </form>
    </Frame>
  );
}
